import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { FLAVORS, priceOf } from '../lib/products';
import { playCrunch } from '../lib/sound';

const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

const key = (slug, variant) => `${slug}:${variant}`;

const toastStyle = {
  background: '#0a0a0a', color: '#F5F3EE', border: '1px solid #FF2A2A',
  borderRadius: 0, fontFamily: 'Archivo', letterSpacing: '0.1em', fontWeight: 800,
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem('krisp-cart')) || []; } catch { return []; }
  });
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(0);

  useEffect(() => { localStorage.setItem('krisp-cart', JSON.stringify(items)); }, [items]);

  const add = useCallback((slug, variant = '100g', qty = 1, silent = false) => {
    setItems((prev) => {
      const k = key(slug, variant);
      const found = prev.find((i) => key(i.slug, i.variant) === k);
      if (found) return prev.map((i) => (key(i.slug, i.variant) === k ? { ...i, qty: Math.min(99, i.qty + qty) } : i));
      return [...prev, { slug, variant, qty }];
    });
    setPulse((p) => p + 1);
    if (!silent) { playCrunch(); toast('CRUNCH ADDED ✓', { style: { ...toastStyle, borderColor: FLAVORS[slug]?.color || '#FF2A2A' } }); }
  }, []);

  const setQty = useCallback((slug, variant, qty) => {
    setItems((prev) => (qty <= 0
      ? prev.filter((i) => key(i.slug, i.variant) !== key(slug, variant))
      : prev.map((i) => (key(i.slug, i.variant) === key(slug, variant) ? { ...i, qty } : i))));
  }, []);

  const remove = useCallback((slug, variant) => {
    setItems((prev) => prev.filter((i) => key(i.slug, i.variant) !== key(slug, variant)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const { count, subtotal } = useMemo(() => items.reduce(
    (acc, i) => ({ count: acc.count + i.qty, subtotal: acc.subtotal + i.qty * priceOf(i.slug, i.variant) }),
    { count: 0, subtotal: 0 },
  ), [items]);

  return (
    <CartContext.Provider value={{ items, add, setQty, remove, clear, open, setOpen, count, subtotal, pulse }}>
      {children}
    </CartContext.Provider>
  );
}
